import type { SetRequired, StrictExtract, StrictOmit } from '@frontend/duck-tape';
import { match } from '@frontend/duck-tape';
import { BetaBadge, Icon, Tooltip } from '@frontend/web-react';
import type { CustomIconName, IconName } from '@frontend/web-react/components/Primitives/Icon/helpers';
import type { ColorVariant } from '@frontend/web-react/types';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, safeCss } from '@frontend/web-utils';
import type { NavLinkProps as MNavLinkProps, MantineColors } from '@mantine/core';
import { NavLink as MNavLink } from '@mantine/core';
import { Link as TLink } from '@tanstack/react-router';
import type { LinkComponentProps } from '@tanstack/react-router/dist/esm/link';

type SidebarNavLinkColor = StrictExtract<ColorVariant, 'danger' | 'primary' | 'secondary'>;

type SidebarNavLinkProps = LinkComponentProps<'a'> &
  StrictOmit<SetRequired<MNavLinkProps, 'label'>, 'active' | 'color'> & {
    activeClassName?: ClassName;
    color?: SidebarNavLinkColor;
    exact?: boolean;
    inactiveClassName?: ClassName;
    // Override when route matching automatically doesn't work as expected
    isActive?: boolean;
    isBeta?: boolean;
    isCollapsed?: boolean;
    leftIconName?: CustomIconName | IconName;
    onClickCallback?: () => void;
    rightIconName?: IconName;
  };

export const SidebarNavLink = ({
  activeClassName = safeCss('bg-cream-200'),
  color = 'secondary',
  exact = false,
  inactiveClassName,
  isActive = false,
  isBeta,
  isCollapsed,
  label,
  leftIconName,
  onClickCallback,
  rightIconName,
  ...props
}: SidebarNavLinkProps) => {
  const resolvedColor = match<SidebarNavLinkColor, MantineColors>(color)
    .with('danger', () => 'red')
    .with('primary', () => 'navy')
    .with('secondary', () => 'navy')
    .exhaustive();

  return (
    <Tooltip classNames={{ tooltip: safeCss('text-textInverse') }} disabled={!isCollapsed} label={label} position="right">
      {/* @ts-expect-error Fake news */}
      <MNavLink
        activeOptions={{
          exact,
          includeSearch: true,
        }}
        activeProps={{
          className: activeClassName,
        }}
        aria-label={label}
        classNames={{
          body: isCollapsed ? '' : safeCss('py-xs'),
          label: getLabelClassName({ color, isActive }),
          root: concatClasses(
            safeCss('sidebar-hover-background-effect gap-x-xs no-underline'),
            isCollapsed
              ? safeCss('size-xxl flex items-center justify-center p-sm rounded-md')
              : safeCss('rounded-md py-sm px-md'),
          ),
        }}
        color={resolvedColor}
        component={TLink}
        inactiveProps={{
          className: inactiveClassName,
        }}
        label={isCollapsed && leftIconName ? <Icon color="secondary" name={leftIconName} /> : label}
        leftSection={isCollapsed ? null : leftIconName ? <Icon color="primary" name={leftIconName} /> : undefined}
        onClick={onClickCallback}
        rightSection={
          isBeta && !isCollapsed ? (
            <BetaBadge />
          ) : rightIconName ? (
            <Icon color="secondary" name={rightIconName} />
          ) : undefined
        }
        role="link"
        {...props}
      />
    </Tooltip>
  );
};

const getLabelClassName = ({ color, isActive }: PickRequired<SidebarNavLinkProps, 'color' | 'isActive'>) => {
  const labelClassMap = {
    danger: safeCss('text-textError'),
    primary: safeCss(isActive ? 'text-textPrimary' : 'text-textTertiary'),
    secondary: safeCss('text-textPrimary'),
  };

  return concatClasses(safeCss(isActive ? 'font-bold' : 'font-medium') + ' text-h6', labelClassMap[color]);
};
